import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { ISharedFileRequestResponse } from "@wingspanhq/shared-files/dist/lib/interfaces";
import { useWSQueries } from "@ws-react-query";
import { useMemo } from "react";
import { QUERY_SHARED_FILE } from "../../../../query/sharedFile/keys";
import { getSharedFile } from "../../../../services/sharedFile/getSharedFile";
import { getDataSourceQueries } from "../../helpers/getDataSourceQueries";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { buildConfigFields } from "../../utils";
import { getSharedFileFromCache } from "./configs/helpers/getSharedFileFromCache";
import { usePayeeSharedFileRequirementConfig } from "./configs/payee/usePayeeSharedFileRequirementConfig";
import { usePayerSharedFileRequirementConfig } from "./configs/payer/usePayerSharedFileRequirementConfig";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = getDataSourceQueries(
    QUERY_SHARED_FILE,
    requirements,
    getSharedFile,
    RequirementType.SharedFile
  );

  return useWSQueries({ queries });
};

export const useSharedFileRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.SharedFile] => {
  const contextQueries = useContextQueries(requirements);

  const payerSharedFileRequirementConfig =
    usePayerSharedFileRequirementConfig(callback);
  const payeeSharedFileRequirementConfig =
    usePayeeSharedFileRequirementConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    buildConfigFields<ISharedFileRequestResponse, RequirementType.SharedFile>({
      payerConfig: payerSharedFileRequirementConfig,
      payeeConfig: payeeSharedFileRequirementConfig,
      getAdditionalData: getSharedFileFromCache
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getSharedFileFromCache(requirement, context);
    },
    isLoading: contextQueries.some(query => query.isLoading),
    dataUpdatedAt: queriesHash
  };
};
